import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.scss';

const PrivacyPolicy = ({Helmet}) => {

    useEffect(()=>{
        window.scroll(0,0)
    },[])

  return (
    <div className="privacy-policy-container">
      <Helmet>
        <title>Privacy Policy | Punjabi Academy</title>
        <meta name="description" content="Privacy Policy for Punjabi Academy - learn how we collect, use and protect your data." />
      </Helmet>

      <h1>Privacy Policy</h1>
      <p className="pp-effective">Effective date: April 13, 2026</p>

      <p>
        Welcome to <strong>Punjabi Academy</strong> ("we", "us", or "our"), a free educational website dedicated
        to helping children and beginners learn the Punjabi language through interactive alphabets, stories,
        quizzes, videos, and worksheets. We are committed to protecting your privacy. This Privacy Policy
        describes what information we collect, how we use it, and your rights regarding that information.
      </p>
      <p>
        By using this website you agree to the terms of this Privacy Policy. If you do not agree,
        please discontinue use of the site.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We may collect the following types of information:</p>
      <ul>
        <li>
          <strong>Usage data:</strong> When you visit our site, we automatically receive standard log
          information such as your IP address, browser type and version, pages visited, time and date
          of your visit, and time spent on pages. This data is used solely for analytics and
          improving the site.
        </li>
        <li>
          <strong>Contact form data:</strong> If you send us a message via our{' '}
          <Link to="/contactus">Contact page</Link>, we collect your name, email address, and message
          content in order to respond to your enquiry. We do not store this data beyond what is
          needed to reply.
        </li>
        <li>
          <strong>Cookies and tracking technologies:</strong> We and our third-party partners use
          cookies and similar technologies. See Section 4 for full details.
        </li>
      </ul>
      <p>
        <strong>We do not require account registration</strong> and do not knowingly collect names,
        addresses, or any personal information beyond what you voluntarily provide via the contact
        form.
      </p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To operate, maintain, and improve the educational content on this site.</li>
        <li>To understand how visitors use the site so we can improve user experience.</li>
        <li>To respond to messages you send us through the contact form.</li>
        <li>To display relevant advertisements through Google AdSense (see Section 5).</li>
        <li>To comply with legal obligations.</li>
      </ul>
      <p>We do not sell, rent, or trade your personal information to third parties.</p>

      <h2>3. Children's Privacy</h2>
      <p>
        This website is designed to be suitable for children learning Punjabi, however it is intended
        to be used under parental guidance. <strong>We do not knowingly collect personal information
        from children under the age of 13</strong> without verifiable parental consent. If you are a
        parent or guardian and believe your child has provided us with personal information, please
        contact us and we will promptly delete it.
      </p>
      <p>
        In compliance with the Children's Online Privacy Protection Act (COPPA) and applicable law,
        any advertising displayed on this site is configured to serve <strong>non-personalised
        ads</strong> in contexts where child-directed content may be present.
      </p>

      <h2>4. Cookies</h2>
      <p>
        Cookies are small text files stored on your device by your browser. We use the following
        types of cookies:
      </p>
      <ul>
        <li>
          <strong>Essential cookies:</strong> Required for the site to function correctly (e.g.
          remembering your session). These cannot be disabled.
        </li>
        <li>
          <strong>Analytics cookies:</strong> We may use Google Analytics to understand aggregate
          usage patterns. Google Analytics sets cookies to collect information about how visitors
          use the site. All data is anonymised — no personally identifiable information is stored.
          You can opt out via the{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
            Google Analytics opt-out browser add-on
          </a>.
        </li>
        <li>
          <strong>Advertising cookies:</strong> Google AdSense may set cookies to show relevant
          advertisements. See Section 5 for more details.
        </li>
      </ul>
      <p>
        You can control or disable cookies through your browser settings. Note that disabling cookies
        may affect some functionality of the site. For more information visit{' '}
        <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">aboutcookies.org</a>.
      </p>

      <h2>5. Advertising — Google AdSense</h2>
      <p>
        We use <strong>Google AdSense</strong> to display advertisements on this website. Google
        AdSense is an advertising service provided by Google LLC. Google uses cookies (including the
        DoubleClick cookie) to serve ads based on your prior visits to this and other websites.
      </p>
      <ul>
        <li>
          Google's use of advertising cookies enables it and its partners to serve ads to you based
          on your visit to our site and/or other sites on the internet.
        </li>
        <li>
          You may opt out of personalised advertising by visiting{' '}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
            Google Ads Settings
          </a>.
        </li>
        <li>
          You may also opt out of a third-party vendor's use of cookies for personalised advertising
          by visiting{' '}
          <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">
            www.aboutads.info/choices
          </a>.
        </li>
        <li>
          For more information on how Google uses data when you use our site, visit:{' '}
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
            How Google uses information from sites that use our services
          </a>.
        </li>
      </ul>

      <h2>6. Third-Party Services</h2>
      <p>This website uses the following third-party services, each with their own privacy policies:</p>
      <ul>
        <li>
          <strong>Google AdSense</strong> — advertising.{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google Privacy Policy
          </a>
        </li>
        <li>
          <strong>Google Analytics</strong> — anonymous usage analytics.{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Google Privacy Policy
          </a>
        </li>
        <li>
          <strong>EmailJS</strong> — used to send contact form messages.{' '}
          <a href="https://www.emailjs.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">
            EmailJS Privacy Policy
          </a>
        </li>
        <li>
          <strong>Internet Archive / external audio sources</strong> — audio files for alphabet
          sounds are served from third-party URLs. We do not control the privacy practices of those
          hosts.
        </li>
      </ul>
      <p>
        These third parties may collect data independently in accordance with their own privacy
        policies. We are not responsible for the content or privacy practices of third-party websites
        linked from this site.
      </p>

      <h2>7. Data Retention</h2>
      <p>
        We retain contact form data only as long as is necessary to respond to your enquiry. Usage
        and analytics data is retained in aggregate, anonymised form and is never linked back to
        individual users.
      </p>

      <h2>8. Your Rights</h2>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Request correction or deletion of your personal data.</li>
        <li>Object to or restrict the processing of your data.</li>
        <li>Withdraw consent at any time where processing is based on consent.</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us via our{' '}
        <Link to="/contactus">Contact page</Link>.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will revise the
        effective date at the top of this page. We encourage you to review this page periodically.
        Continued use of the site after changes constitutes your acceptance of the updated policy.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy Policy, please reach
        out via our <Link to="/contactus">Contact page</Link>. We aim to respond within 5 business days.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
